import {
  ExclamationTriangleIcon,
  GlobeAltIcon,
  ShieldExclamationIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  TagIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/utils/cn';
import type { ThreatFeedItem } from '@/services/threat-intelligence.service';

interface ThreatFeedItemCardProps {
  item: ThreatFeedItem;
  onViewDetails?: (item: ThreatFeedItem) => void;
}

export default function ThreatFeedItemCard({ item, onViewDetails }: ThreatFeedItemCardProps) {
  const threatColors = {
    low: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
    medium: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
    high: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800',
    critical: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
  };

  const reliabilityColors: Record<string, string> = {
    A: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    B: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    C: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    D: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    E: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
    F: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  };

  const categoryIcons: Record<string, typeof ShieldExclamationIcon> = {
    cyber: ShieldExclamationIcon,
    physical: ExclamationTriangleIcon,
    personnel: EyeIcon,
    reputational: DocumentTextIcon,
    financial: ChartBarIcon,
    operational: GlobeAltIcon,
  };

  const CategoryIcon = categoryIcons[item.category ?? 'operational'] ?? GlobeAltIcon;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {return 'Just now';}
    if (diffInMinutes < 60) {return `${diffInMinutes}m ago`;}
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {return `${diffInHours}h ago`;}
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {return `${diffInDays}d ago`;}

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent ?? '';
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) {return text;}
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <Card hover="default" className="h-full flex flex-col transition-all duration-200">
      <CardContent className="p-6 flex-1 flex flex-col">
        {/* Header: Source info and threat level */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <CategoryIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                {item.sourceName}
              </span>
              <div className="flex items-center space-x-2 mt-0.5">
                {item.sourceReliability && (
                  <span className={cn(
                    'px-1.5 py-0.5 text-xs font-medium rounded',
                    reliabilityColors[item.sourceReliability] || reliabilityColors.C
                  )}>
                    {item.sourceReliability}
                  </span>
                )}
                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                  {formatDate(item.publishedDate)}
                </span>
              </div>
            </div>
          </div>

          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors flex-shrink-0"
              aria-label="Open original article"
            >
              <ArrowTopRightOnSquareIcon className="w-5 h-5" />
            </a>
          )}
        </div>

        {/* Title */}
        <h3 className="font-semibold text-secondary-900 dark:text-white mb-2 line-clamp-2">
          {item.title}
        </h3>

        {/* Description */}
        {item.description && (
          <p className="text-secondary-600 dark:text-secondary-400 text-sm mb-3 line-clamp-3">
            {truncateText(stripHtml(item.description), 200)}
          </p>
        )}

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          {item.threatLevel && (
            <span className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-full border',
              threatColors[item.threatLevel]
            )}>
              {item.threatLevel.toUpperCase()}
            </span>
          )}
          {item.category && (
            <span className="px-2.5 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
              {item.category.toUpperCase()}
            </span>
          )}
        </div>

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex items-center gap-1 mb-3">
            <TagIcon className="w-3.5 h-3.5 text-secondary-400" />
            <div className="flex flex-wrap gap-1">
              {item.tags.slice(0, 4).map((tag, index) => (
                <span
                  key={index}
                  className="px-1.5 py-0.5 text-xs bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 rounded"
                >
                  {tag}
                </span>
              ))}
              {item.tags.length > 4 && (
                <span className="px-1.5 py-0.5 text-xs bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400 rounded">
                  +{item.tags.length - 4}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Indicators */}
        {item.indicators && item.indicators.length > 0 && (
          <div className="mt-3 pt-3 border-t border-secondary-200 dark:border-secondary-700">
            <div className="text-xs text-secondary-500 dark:text-secondary-400 mb-1.5">
              Indicators ({item.indicators.length}):
            </div>
            <div className="flex flex-wrap gap-1">
              {item.indicators.slice(0, 3).map((indicator, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs font-mono bg-warning-50 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300 rounded"
                >
                  {truncateText(indicator, 30)}
                </span>
              ))}
              {item.indicators.length > 3 && (
                <span className="px-2 py-1 text-xs bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400 rounded">
                  +{item.indicators.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* View Details Button (optional) */}
        {onViewDetails && (
          <button
            onClick={() => onViewDetails(item)}
            className="mt-4 w-full py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
          >
            View Details
          </button>
        )}
      </CardContent>
    </Card>
  );
}
